import { useTranslation } from 'react-i18next';
import { ShieldCheck, Building2, Lock } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

const SECURITY_ITEMS = [
  { key: 'permissions', Icon: ShieldCheck },
  { key: 'enterprise', Icon: Building2 },
  { key: 'data', Icon: Lock },
] as const;

export default function SecurityTransparency() {
  const { t } = useTranslation();
  const primaryItem = SECURITY_ITEMS[0];
  const PrimaryIcon = primaryItem.Icon;

  return (
    <section className="py-24 md:py-32 bg-[#faf9f5] dark:bg-[#141413]">
      <div className="mx-auto max-w-6xl px-6">
        {/* Section header */}
        <Reveal className="mb-16 text-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('security.title')}
          </span>
          <h2 className="font-display display-tight mx-auto mt-5 max-w-2xl text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('security.subtitle')}
          </h2>
        </Reveal>

        {/* Non-symmetric bento: large left card + two stacked right cards */}
        <div className="grid gap-4 md:grid-cols-5">
          {/* Primary card — spans 3 cols, double-bezel treatment */}
          <Reveal className="md:col-span-3">
            <div className="h-full rounded-[1.5rem] border border-[#1414131a] dark:border-[#faf9f51a] bg-[#1414130a] dark:bg-[#faf9f50a] p-1.5 shadow-warm">
              <div className="flex h-full flex-col justify-between rounded-[calc(1.5rem-0.375rem)] bg-[#fffefb] dark:bg-[#1a1918] p-8 shadow-inner-glow">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ae5630]/10 dark:bg-[#ae5630]/20 text-[#ae5630]">
                  <PrimaryIcon size={22} />
                </div>
                <div>
                  <h3 className="font-display display-tight text-2xl font-medium text-[#141413] dark:text-[#faf9f5] mb-3">
                    {t(`security.${primaryItem.key}.title`)}
                  </h3>
                  <p className="text-[#6b6a68] dark:text-[#9a9893] leading-relaxed">
                    {t(`security.${primaryItem.key}.desc`)}
                  </p>
                </div>
                {/* Decorative element */}
                <div className="mt-8 flex gap-2" aria-hidden="true">
                  <span className="h-1 w-8 rounded-full bg-[#ae5630]/40" />
                  <span className="h-1 w-4 rounded-full bg-[#ae5630]/20" />
                  <span className="h-1 w-2 rounded-full bg-[#ae5630]/10" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right column: two smaller cards */}
          <RevealGroup className="md:col-span-2 flex flex-col gap-4">
            {SECURITY_ITEMS.slice(1).map(({ key, Icon }) => (
              <RevealItem key={key}>
                <div className="group rounded-2xl border border-[#1414131a] dark:border-[#faf9f51a] bg-[#fffefb] dark:bg-[#1a1918] p-6 shadow-warm transition-all duration-300 ease-spring hover:shadow-warm-lg hover:-translate-y-0.5">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#ae5630]/10 dark:bg-[#ae5630]/20 text-[#ae5630] transition-colors duration-300 group-hover:bg-[#ae5630]/20">
                    <Icon size={19} />
                  </div>
                  <h3 className="text-base font-semibold text-[#141413] dark:text-[#faf9f5] mb-2">
                    {t(`security.${key}.title`)}
                  </h3>
                  <p className="text-sm text-[#6b6a68] dark:text-[#9a9893] leading-relaxed">
                    {t(`security.${key}.desc`)}
                  </p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </div>
    </section>
  );
}
